import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { CircleX, PencilLine, Save, Trash2, Upload } from "lucide-react";
import ReactCrop, { PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import ImageView from "../../../ai-content/components/ImageView";
import CollapsibleCard from "../../../components/ui/CollapsibleCard";
import { uploadFileLogActivity } from "../../../api/ProjectApi";
import { base64ToFile, sanitizeFileName } from "../../../ai-content/helpers";
import LayoutAction from "../../../actions/LayoutAction";
import ConfirmDialog from "../../../dialog/ConfirmDialog";
import OctagonAlertIcon from "../../../icons/OctagonAlertIcon";
import TriangleAlertIcon from "../../../icons/TriangleAlertIcon";

const MAX_SIZE = 3 * 1024 * 1024; // 3MB

interface ImagesPanelProps {
  itemId: string;
  value: string[];
  onChange: (value: string[]) => void;
}

const ImagesPanel = ({ itemId, value, onChange }: ImagesPanelProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [src, setSrc] = useState<string | null>(null);
  const defaultCrop: PixelCrop = {
    unit: "px",
    width: 1080,
    height: 1080,
    x: 1080,
    y: 1080,
  };
  const [crop, setCrop] = useState<PixelCrop>(defaultCrop);
  const [filenameImage, setFilenameImage] = useState<string | null>(null);
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const [error, setError] = useState<string>("");
  const imgRef = useRef<HTMLImageElement | null>(null);

  const [selectedImageId, setSelectedImageId] = useState("");
  const [showConfirm, setShowConfirm] = useState<any>({
    show: false,
    title: "",
    description: "",
  });

  useEffect(() => {
    if (value && value.length > 0) {
      setSelectedImageId(value[0]);
    } else {
      setSelectedImageId("");
    }
  }, [value]);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setSrc(ev.target?.result as string);
        setError("");
        setFilenameImage(sanitizeFileName(Date.now() + "-" + file.name));
        setSelectedImageId("");
      };
      reader.readAsDataURL(file);
    }
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const reader = new FileReader();
      reader.onload = (ev) => {
        setSrc(ev.target?.result as string);
        setError("");
      };
      reader.readAsDataURL(file);
    }
  };

  const onImageLoaded = (img: HTMLImageElement) => {
    imgRef.current = img;
    const displayWidth = img.width;
    const displayHeight = img.height;
    const minSide = Math.min(displayWidth, displayHeight) - 32;
    const x = (displayWidth - minSide) / 2;
    const y = (displayHeight - minSide) / 2;
    const defaultCrop = {
      unit: "px",
      width: minSide,
      height: minSide,
      x,
      y,
    };
    setCrop(defaultCrop as PixelCrop);
    setTimeout(() => {
      makeClientCrop(defaultCrop as PixelCrop);
    }, 0);
    return false;
  };

  const makeClientCrop = async (crop: PixelCrop) => {
    if (imgRef.current && crop.width && crop.height) {
      const result = await getCroppedImg(imgRef.current, crop);
      setCroppedImage(result.base64);
      if (result.base64 && result.base64.length > MAX_SIZE * 1.37) {
        setError(
          t(
            "The cropped image still exceeds 3MB after optimization. Please try a different image or crop area."
          )
        );
      } else {
        setError("");
      }
    }
  };

  function getCroppedImg(
    image: HTMLImageElement,
    crop: PixelCrop
  ): Promise<{ base64: string; optimizationInfo: string }> {
    return new Promise((resolve) => {
      let targetSize = 1080;
      let quality = 0.92;
      let base64 = "";
      let originalSize = 1080;
      let wasOptimized = false;

      function tryCompress() {
        const canvas = document.createElement("canvas");
        canvas.width = targetSize;
        canvas.height = targetSize;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve({ base64: "", optimizationInfo: "" });
          return;
        }

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        ctx.drawImage(
          image,
          crop.x * scaleX,
          crop.y * scaleY,
          crop.width * scaleX,
          crop.height * scaleY,
          0,
          0,
          targetSize,
          targetSize
        );

        base64 = canvas.toDataURL("image/png", quality);

        // Nếu ảnh vẫn quá lớn và chưa giảm kích thước xuống mức tối thiểu
        if (base64.length > MAX_SIZE * 1.37 && targetSize > 60) {
          // Giảm kích thước ảnh
          targetSize = Math.max(60, targetSize - 20);
          quality = 0.92; // Reset quality khi giảm kích thước
          wasOptimized = true;
          tryCompress();
        } else if (base64.length > MAX_SIZE * 1.37 && quality > 0.3) {
          // Nếu đã giảm kích thước tối thiểu, giảm chất lượng
          quality -= 0.05;
          wasOptimized = true;
          tryCompress();
        } else {
          let optimizationInfo = "";
          if (wasOptimized) {
            if (targetSize < originalSize) {
              optimizationInfo =
                t("Image optimized: size reduced to") + ` ${targetSize}x${targetSize}`;
            } else if (quality < 0.92) {
              optimizationInfo =
                t("Image optimized: quality reduced to") + ` ${Math.round(quality * 100)}%`;
            }
          }
          resolve({ base64, optimizationInfo });
        }
      }

      tryCompress();
    });
  }

  const handleSave = () => {
    if (croppedImage && filenameImage) {
      if (croppedImage.length <= MAX_SIZE * 1.37) {
        uploadFileLogActivity(itemId, base64ToFile(croppedImage, filenameImage), "Shared").then(
          () => {
            onChange([filenameImage]);
            setError("");
            dispatch(
              LayoutAction.setNotification({
                title: t("Image"),
                type: "success",
                message: t("Uploaded image"),
              })
            );
          }
        );
      } else {
        setError(
          t(
            "The image must be less than or equal to 3MB. Please try a different image or crop area."
          )
        );
      }
      // If select from list image
    } else {
      setError(t("Please upload an image or select an image."));
    }
  };

  const handleChange = () => {
    document.getElementById("image-upload-input")?.click();
  };

  const handleDelete = () => {
    setShowConfirm({
      show: true,
      title: t("Delete image"),
      description: t("Are you sure you want to remove this image?"),
      alertIcon: <TriangleAlertIcon />,
      dangerBtn: true,
      type: "DELETE",
    });
  };

  const handleSubmitConfirm = () => {
    if (showConfirm.type === "DELETE") {
      onChange([]);
      // Reset image
      setSrc(null);
      setCrop(defaultCrop);
      setCroppedImage(null);
      setError("");
      setFilenameImage(null);
      imgRef.current = null;
    }
    setShowConfirm({ show: false, title: "", description: "" });
  };

  return (
    <>
      <CollapsibleCard
        title={t("Image")}
        defaultCollapsed={false}
        cardProps={{
          bgClass: "bg-white",
          borderClass: "border border-gray-200",
          roundedClass: "rounded-[12px]",
          shadow: "custom",
          shadowClass: "shadow-[0_1px_1px_0_rgba(0,0,0,0.05)]",
        }}
      >
        {selectedImageId ? (
          <div className="flex flex-col items-center justify-center">
            <div className="group relative w-60 h-60 flex flex-col items-center justify-center py-2 mx-4 border border-gray-300 rounded-xl mb-3">
              <ImageView fileName={selectedImageId} className="w-full h-full object-cover" />
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="flex items-center gap-2">
                  <div
                    className="bg-blue-500 flex items-center gap-1 px-1.5 py-0.5 rounded-md cursor-pointer"
                    onClick={handleChange}
                  >
                    <PencilLine className="w-3 h-3 text-white" />
                    <span className="text-[10px] font-medium text-white">{t("Change image")}</span>
                  </div>
                  <div
                    className="bg-red-500 flex items-center gap-1 px-1.5 py-0.5 rounded-md cursor-pointer"
                    onClick={handleDelete}
                  >
                    <CircleX className="w-3 h-3 text-white" />
                    <span className="text-[10px] font-medium text-white">{t("Remove")}</span>
                  </div>
                </div>
              </div>
              <input
                id="image-upload-input"
                type="file"
                accept="image/*"
                onChange={onFileChange}
                className="hidden"
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center">
            {!src && (
              <div
                className="group w-60 h-60 flex flex-col items-center justify-center py-2 mx-4 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 cursor-pointer transition hover:border-blue-400 mb-3"
                onDrop={onDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => document.getElementById("image-upload-input")?.click()}
              >
                <input
                  id="image-upload-input"
                  type="file"
                  accept="image/*"
                  onChange={onFileChange}
                  className="hidden"
                />
                <div>
                  <Upload className="w-6 h-6 text-gray-500 group-hover:text-blue-400" />
                </div>
                <div className="px-4 py-2 text-xs text-gray-500 text-center">
                  {t("Drop a file to upload or click to select")}
                </div>
                <div className="px-4 py-2 text-xs text-gray-500 text-center">
                  {t("Use image with 1080x1080px and up to 3MB")}
                </div>
              </div>
            )}
            {src && (
              <div className="w-full mb-4 flex flex-col items-center rounded-lg">
                <div className="w-120 h-120 flex flex-col items-center justify-center rounded-lg bg-gray-100 cursor-pointer">
                  <ReactCrop
                    crop={crop}
                    onChange={(newCrop) => setCrop(newCrop)}
                    onComplete={makeClientCrop}
                    aspect={1}
                  >
                    <img
                      src={src}
                      onLoad={(e) => onImageLoaded(e.currentTarget as HTMLImageElement)}
                      alt="image"
                      className="max-w-full max-h-full bg-white"
                    />
                  </ReactCrop>
                </div>
                <div className="w-full mt-2 flex items-center justify-center gap-2">
                  <div
                    className="bg-red-500 flex items-center gap-1 px-1.5 py-1 rounded-md cursor-pointer"
                    onClick={() => {
                      setSrc(null);
                      setCrop(defaultCrop);
                      setCroppedImage(null);
                      setError("");
                      setFilenameImage(null);
                      imgRef.current = null;
                      if (value && value.length > 0) {
                        setSelectedImageId(value[0]);
                      } else {
                        setSelectedImageId("");
                      }
                    }}
                  >
                    <Trash2 className="w-2.5 h-2.5 text-white" />
                    <span className="text-xs font-medium text-white">{t("Remove")}</span>
                  </div>
                  <div
                    className="bg-blue-500 flex items-center gap-1 px-1.5 py-1 rounded-md cursor-pointer"
                    onClick={handleSave}
                  >
                    <Save className="w-2.5 h-2.5 text-white" />
                    <span className="text-xs font-medium text-white">{t("Save")}</span>
                  </div>
                </div>
              </div>
            )}
            {error && <div className="text-red-600 text-xs mt-1 text-center">{error}</div>}
          </div>
        )}
      </CollapsibleCard>
      <ConfirmDialog
        visible={showConfirm.show}
        title={showConfirm.title}
        description={showConfirm.description}
        alertIcon={showConfirm.alertIcon || <OctagonAlertIcon />}
        dangerBtn={showConfirm.dangerBtn}
        onSubmit={handleSubmitConfirm}
        onClose={() => {
          setShowConfirm({ show: false, title: "", description: "" });
        }}
      />
    </>
  );
};

export default ImagesPanel;
